
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Phone, MapPin, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCoordinatorFarms, useCoordinatorStats } from '@/hooks/use-coordinator';

const CoordinatorFarmerManagement: React.FC = () => {
  const { data: farms = [], isLoading } = useCoordinatorFarms();
  const { data: stats } = useCoordinatorStats();
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredFarms = farms.filter(farm =>
    farm.farmer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    farm.region.toLowerCase().includes(searchTerm.toLowerCase()) ||
    farm.phone_number.includes(searchTerm)
  );

  if (isLoading) {
    return <div className="p-6">Loading farmers...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Farmer Management</h1>
          <p className="text-muted-foreground">Manage and coordinate with farmers</p>
        </div>
        <Button asChild>
          <Link to="/farm-form">
            <Plus className="h-4 w-4 mr-2" />
            Add New Farmer
          </Link>
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search farmers by name, region, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats?.totalFarms || 0}</div>
            <p className="text-sm text-muted-foreground">Total Farmers Registered</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {stats?.farmsWithPhoneAccess || 0}
            </div>
            <p className="text-sm text-muted-foreground">With Phone Access</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {stats?.farmsWithContracts || 0}
            </div>
            <p className="text-sm text-muted-foreground">With Contracts</p>
          </CardContent>
        </Card>
      </div>

      {/* Farmers List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredFarms.map((farm) => (
          <Card key={farm.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  {farm.farmer_name}
                </CardTitle>
                <div className="flex gap-1">
                  {farm.has_phone_access && (
                    <Badge variant="secondary" className="text-xs">Phone</Badge>
                  )}
                  {farm.has_contract && (
                    <Badge variant="success" className="text-xs">Contract</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center text-sm">
                <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                {farm.phone_number}
              </div>
              <div className="flex items-center text-sm">
                <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                {farm.region}
                {farm.kebelle && `, ${farm.kebelle}`}
              </div>
              <div className="text-sm text-muted-foreground">
                Land Size: {farm.land_size} hectares
              </div>
              {farm.product_name && (
                <div className="text-sm">
                  <Badge variant="outline">{farm.product_name}</Badge>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button asChild size="sm" variant="outline" className="flex-1">
                  <Link to={`/farm-detail/${farm.id}`}>
                    View Details
                  </Link>
                </Button>
                <Button asChild size="sm" className="flex-1">
                  <Link to={`/farm-form?edit=${farm.id}`}>
                    Edit
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredFarms.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No farmers found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? 'Try adjusting your search terms' : 'Start by adding your first farmer'}
            </p>
            <Button asChild>
              <Link to="/farm-form">
                <Plus className="h-4 w-4 mr-2" />
                Add First Farmer
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CoordinatorFarmerManagement;
